import React from 'react';
import { Container, Group, Title, Button, Table, Badge, Modal, Stack, Select, NumberInput, Textarea, Skeleton, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconShield, IconPlus } from '@tabler/icons-react';
import { useWarrantyRegistrations, useWarrantyClaims, useSubmitWarrantyClaim } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  expired: 'gray',
  pending: 'yellow',
  approved: 'blue',
  resolved: 'green',
  denied: 'red',
};

export default function WarrantyPage() {
  const [opened, { open, close }] = useDisclosure(false);
  const { data: warranties, isLoading: loadingWarranties } = useWarrantyRegistrations();
  const { data: claims, isLoading: loadingClaims } = useWarrantyClaims();
  const submitClaim = useSubmitWarrantyClaim();

  const [formData, setFormData] = React.useState({
    registration_id: '',
    claim_type: '',
    amount: 0,
    description: '',
  });

  const handleSubmit = async () => {
    try {
      await submitClaim.mutateAsync({
        registration_id: Number(formData.registration_id),
        claim_type: formData.claim_type,
        claimed_amount: formData.amount,
        description: formData.description,
      });
      notifications.show({
        title: 'Success',
        message: 'Warranty claim submitted successfully',
        color: 'green',
      });
      close();
      setFormData({ registration_id: '', claim_type: '', amount: 0, description: '' });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to submit warranty claim',
        color: 'red',
      });
    }
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Group>
          <IconShield size={28} />
          <Title order={2}>Warranty Claims</Title>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={open}>
          Submit Claim
        </Button>
      </Group>

      <Title order={3} mb="md">My Warranties</Title>
      {loadingWarranties ? (
        <Stack gap="xs">
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Stack>
      ) : warranties && warranties.length > 0 ? (
        <Table striped highlightOnHover mb="xl">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Product</Table.Th>
              <Table.Th>Policy</Table.Th>
              <Table.Th>Registration Date</Table.Th>
              <Table.Th>Expiration Date</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {warranties.map((warranty: any) => (
              <Table.Tr key={warranty.id}>
                <Table.Td>{warranty.product}</Table.Td>
                <Table.Td>{warranty.policy}</Table.Td>
                <Table.Td>{new Date(warranty.registration_date).toLocaleDateString()}</Table.Td>
                <Table.Td>{new Date(warranty.expiration_date).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[warranty.status]} variant="light">
                    {warranty.status}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Text ta="center" c="dimmed" py="xl">No warranties found</Text>
      )}

      <Title order={3} mb="md">My Claims</Title>
      {loadingClaims ? (
        <Stack gap="xs">
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Stack>
      ) : claims && claims.length > 0 ? (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Claim #</Table.Th>
              <Table.Th>Warranty</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Date</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {claims.map((claim: any) => (
              <Table.Tr key={claim.id}>
                <Table.Td>{claim.claim_number}</Table.Td>
                <Table.Td>{claim.warranty}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{claim.claim_type}</Badge>
                </Table.Td>
                <Table.Td>${claim.amount.toFixed(2)}</Table.Td>
                <Table.Td>
                  <Badge color={STATUS_COLORS[claim.status]} variant="light">
                    {claim.status}
                  </Badge>
                </Table.Td>
                <Table.Td>{new Date(claim.created_at).toLocaleDateString()}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Text ta="center" c="dimmed" py="xl">No claims found</Text>
      )}

      <Modal opened={opened} onClose={close} title="Submit Warranty Claim" size="md">
        <Stack>
          <Select
            label="Warranty Registration"
            placeholder="Select warranty"
            data={warranties?.map((w: any) => ({ value: w.id, label: `${w.product} - ${w.policy}` })) || []}
            value={formData.registration_id}
            onChange={(value) => setFormData({ ...formData, registration_id: value || '' })}
            required
          />
          <Select
            label="Claim Type"
            placeholder="Select type"
            data={[
              { value: 'defect', label: 'Defect' },
              { value: 'malfunction', label: 'Malfunction' },
              { value: 'damage', label: 'Damage' },
              { value: 'other', label: 'Other' },
            ]}
            value={formData.claim_type}
            onChange={(value) => setFormData({ ...formData, claim_type: value || '' })}
            required
          />
          <NumberInput
            label="Amount"
            placeholder="0.00"
            prefix="$"
            decimalScale={2}
            value={formData.amount}
            onChange={(value) => setFormData({ ...formData, amount: Number(value) || 0 })}
            required
          />
          <Textarea
            label="Description"
            placeholder="Describe the issue..."
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
          <Button onClick={handleSubmit} loading={submitClaim.isPending}>
            Submit Claim
          </Button>
        </Stack>
      </Modal>
    </Container>
  );
}
